import {colors, headerSizes, zIndex, tokens} from '@commercial-helios/ui/theme'
import React from 'react'
import {RemoveScroll} from 'react-remove-scroll'
import styled, {css} from 'styled-components'

import type {Props as SliderProps} from '@/components/slider'
import Slider from '@/components/slider'
import useMediaQuery from '@helios/shared/hooks/use-media-query'
import {Section, Layout, container} from '@helios/shared/styles/grid'
import {Text, TextLink} from '@helios/shared/styles/typography'
import mq from '@helios/shared/utils/media-query'

import {Results} from '../results'
import type {Location, LocationNames} from '../shared'
import {getReplacePlaceholders, MOBILE_BREAKPOINT} from '../shared'

type SliderGroup = Omit<SliderProps, 'withNumberInput'> & {
    heading: string
}

type PackageItem = {
    title: string
    value: number
}

export type Props = {
    title: string
    subtitle: string
    sliders: SliderGroup[]
    budgetTotal: number
    budgetUsed: number
    location: Location
    showResultsText: string
    hideResultsText: string
    resultsTitle: string
    resultsBudgetAllowanceText: string
    resultsPackageTitle: string
    resultsProgressBarText: string
    resultsPackageItems: PackageItem[]
    resultsTotalBenefitsTitle: string
    resultsNote: string
    locationNames: LocationNames
}

export const SlidersAndResults = ({
    title,
    subtitle,
    sliders,
    budgetTotal,
    budgetUsed,
    location,
    showResultsText,
    hideResultsText,
    resultsTitle,
    resultsBudgetAllowanceText,
    resultsPackageTitle,
    resultsProgressBarText,
    resultsPackageItems,
    resultsTotalBenefitsTitle,
    resultsNote,
    locationNames,
}: Props) => {
    const [showResults, setShowResults] = React.useState(false)
    const isSmallScreen = useMediaQuery.max(MOBILE_BREAKPOINT)

    const replacePlaceholders = getReplacePlaceholders({
        location,
        budgetTotal,
        budgetUsed,
        locationNames,
    })

    return (
        <Section>
            <Layout variant="two-asymmetrical">
                <SlidersAndTitles>
                    <Titles>
                        <Text as="h2" variant="title-base">
                            {replacePlaceholders(title)}
                        </Text>

                        <Text variant="paragraph-base" color="muted">
                            {subtitle}
                        </Text>
                    </Titles>

                    {!showResults && isSmallScreen && (
                        <ShowResultsButtonWrapper>
                            <TextLink as="button" onClick={() => setShowResults(true)}>
                                {showResultsText}
                            </TextLink>
                        </ShowResultsButtonWrapper>
                    )}

                    <Sliders>
                        {sliders.map(({heading, label, ...sliderProps}, i) => (
                            <SliderWrapper key={i}>
                                <Text variant="paragraph-base" color="brand">
                                    {replacePlaceholders(heading)}
                                </Text>

                                <Slider
                                    {...sliderProps}
                                    withNumberInput
                                    label={replacePlaceholders(label ?? '').join('')}
                                />
                            </SliderWrapper>
                        ))}
                    </Sliders>
                </SlidersAndTitles>

                {(showResults || !isSmallScreen) && (
                    <ResultsWrapper enabled={showResults && isSmallScreen}>
                        <HideResultsButtonWrapper>
                            <TextLink as="button" onClick={() => setShowResults(false)}>
                                {hideResultsText}
                            </TextLink>
                        </HideResultsButtonWrapper>

                        <StyledResults
                            title={resultsTitle}
                            budgetTotal={budgetTotal}
                            budgetUsed={budgetUsed}
                            budgetAllowanceText={resultsBudgetAllowanceText}
                            packageTitle={resultsPackageTitle}
                            location={location}
                            progressBarText={resultsProgressBarText}
                            packageItems={resultsPackageItems}
                            totalBenefitsTitle={resultsTotalBenefitsTitle}
                            note={resultsNote}
                            locationNames={locationNames}
                        />
                    </ResultsWrapper>
                )}
            </Layout>
        </Section>
    )
}

const SlidersAndTitles = styled.div`
    display: grid;

    ${mq.min(MOBILE_BREAKPOINT)} {
        row-gap: 100px;
    }
`

const Titles = styled.div`
    display: grid;
    row-gap: ${tokens.spacing20};
`

const Sliders = styled.div`
    display: grid;
    row-gap: 100px;
    align-content: start;

    ${mq.min(MOBILE_BREAKPOINT)} {
        row-gap: 80px;
    }
`

const SliderWrapper = styled.div`
    display: grid;
    row-gap: ${tokens.spacing4};
`

const ResultsWrapper = styled(RemoveScroll)`
    width: 100%;

    ${mq.max(MOBILE_BREAKPOINT)} {
        position: fixed;
        top: 0;
        left: 0;
        height: 100vh;
        overflow-y: auto;
        background: ${colors.background.default};
    }

    ${mq.min(MOBILE_BREAKPOINT)} {
        position: sticky;
        top: ${headerSizes.heightSmall}px;
    }

    ${mq.min(headerSizes.breakpoint)} {
        top: ${headerSizes.heightLarge}px;
    }
`

const resultsButtonWrapperStyles = css`
    position: sticky;
    top: ${headerSizes.heightSmall}px;
    z-index: ${zIndex.flexBenefitsShowResultsBtn};
    padding-top: ${tokens.spacing16};
    padding-bottom: ${tokens.spacing16};
    text-align: right;
    background: ${colors.background.default};

    ${mq.min(MOBILE_BREAKPOINT)} {
        display: none;
    }
`

const ShowResultsButtonWrapper = styled.div`
    ${resultsButtonWrapperStyles}

    margin-top: ${tokens.spacing40};
    margin-bottom: ${tokens.spacing40};
`

const HideResultsButtonWrapper = styled.div`
    ${resultsButtonWrapperStyles}
    ${container}
`

const StyledResults = styled(Results)`
    ${mq.max(MOBILE_BREAKPOINT)} {
        position: relative;
        top: ${headerSizes.heightSmall}px;
    }
`
